import { useEffect, useState } from 'react';
import type { StorefrontSection } from '@mlm/shared';
import { useStorefrontContent } from '../../lib/hooks';
import { bannerStyle, hasBannerText } from '../../lib/banner-style';

type HeroConfig = Extract<StorefrontSection, { type: 'hero' }>['config'];

// Brand gradient follows the tenant (`--brand-gradient`; baseline byte-identical).
const O_GRAD = 'var(--brand-gradient)';

/**
 * Rotating hero carousel (reads cfg.heroSlides) + optional stacked side tiles.
 * Extracted verbatim from the former home.tsx hero block.
 */
export function HeroSection({ section }: { section: StorefrontSection }) {
  const cfg = section.config as HeroConfig;
  const { heroSlides, homeSideBanners } = useStorefrontContent().data;

  const [slide, setSlide] = useState(0);
  const slideCount = heroSlides.length;
  const advanceMs = cfg.autoAdvanceMs ?? 2600;
  useEffect(() => {
    if (slideCount <= 1) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % slideCount), advanceMs);
    return () => clearInterval(id);
  }, [slideCount, advanceMs]);
  const hero = heroSlides[slide % Math.max(slideCount, 1)];
  const showSide = cfg.sideBanners ?? false;
  // A slide with every caption field blank is a FULL-IMAGE banner: the artwork
  // carries its own copy, so nothing is drawn over it and it isn't dimmed.
  const captioned = hasBannerText(hero?.kicker, hero?.title, hero?.highlight, hero?.date);

  return (
    <section className="mt-3.5 grid gap-3 lg:grid-cols-[2.2fr_1fr]">
      <div
        className="relative flex min-h-[240px] flex-col justify-center overflow-hidden rounded-lg p-10 text-white"
        style={bannerStyle({ imageKey: hero?.imageKey, gradient: O_GRAD, dim: captioned })}
      >
        {hero && captioned && (
          <>
            {/* Each field renders only when it has text — an empty `date` used to
                paint a bare white chip, and the highlight line carried a
                hardcoded "up to" that read as nonsense for most copy ("up to
                Nationwide") and doubled up on any that said it themselves. */}
            {hero.kicker && (
              <div className="text-[13px] font-extrabold uppercase tracking-[0.18em] opacity-90">
                {hero.kicker}
              </div>
            )}
            {(hero.title || hero.highlight) && (
              <h2 className="mt-2 text-[46px] font-extrabold leading-[0.98] tracking-[-0.02em]">
                {hero.title}
                {hero.title && hero.highlight && <br />}
                {hero.highlight && <span className="text-[#FFE08A]">{hero.highlight}</span>}
              </h2>
            )}
            {hero.date && (
              <div className="mt-3.5 self-start rounded-[4px] bg-white px-3.5 py-[7px] text-[15px] font-extrabold text-o">
                {hero.date}
              </div>
            )}
          </>
        )}
        {slideCount > 1 && (
          <div className="absolute bottom-[18px] left-10 flex gap-1.5">
            {heroSlides.map((_, i) => (
              <i
                key={i}
                className={`block h-[7px] rounded-full transition-all ${
                  i === slide % slideCount ? 'w-[18px] bg-white' : 'w-[7px] bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
      {showSide && (
        <div className="grid grid-rows-2 gap-3">
          {homeSideBanners.map((b, i) => (
            <div
              key={`${b.title}-${i}`}
              className="flex flex-col justify-end overflow-hidden rounded-lg p-[18px] text-white shadow-[0_1px_3px_rgba(0,0,0,.05)]"
              style={bannerStyle({
                imageKey: b.imageKey,
                gradient: b.gradient,
                dim: hasBannerText(b.title, b.sub),
              })}
            >
              {b.title && <div className="text-[15px] font-bold">{b.title}</div>}
              {b.sub && <div className="mt-[3px] text-[12.5px] opacity-90">{b.sub}</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
